import { loadUPlot } from '@/lib/uplot-lazy';
import { registerCleanup } from '@/router';
import { h } from '@/lib/dom';
import type uPlot from 'uplot';

export interface SingleLineChartProps {
  area: HTMLElement;
  xs: number[];
  values: Array<number | null>;
  label: string;
  unit: string;
  color?: string;
  fractionDigits?: number;
  /** Show linear-regression trend line as a dashed series (default true). */
  showTrend?: boolean;
}

const SHORT_MONTHS = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

function shortDate(tsSec: number): string {
  const d = new Date(tsSec * 1000);
  return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}`;
}

function fullDate(tsSec: number): string {
  const d = new Date(tsSec * 1000);
  return `${d.getDate()} ${SHORT_MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

function getVar(name: string, fallback: string): string {
  if (typeof getComputedStyle === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return v || fallback;
}

/** Linear least-squares regression over (x_i, y_i) pairs, ignoring null y. */
function trendLine(xs: number[], ys: Array<number | null>): Array<number | null> {
  const pairs: Array<[number, number]> = [];
  for (let i = 0; i < xs.length; i++) {
    const y = ys[i];
    if (y != null && Number.isFinite(y)) pairs.push([xs[i]!, y]);
  }
  if (pairs.length < 2) return ys.map(() => null);
  const n = pairs.length;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (const [x, y] of pairs) {
    sumX += x; sumY += y; sumXY += x * y; sumXX += x * x;
  }
  const denom = n * sumXX - sumX * sumX;
  if (denom === 0) return ys.map(() => null);
  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return xs.map((x) => slope * x + intercept);
}

export async function mountSingleLineChart({
  area,
  xs,
  values,
  label,
  unit,
  color,
  fractionDigits,
  showTrend = true,
}: SingleLineChartProps): Promise<void> {
  if (!area.isConnected) {
    queueMicrotask(() => {
      void mountSingleLineChart({ area, xs, values, label, unit, color, fractionDigits, showTrend });
    });
    return;
  }
  const uPlotCtor = await loadUPlot();
  if (!area.isConnected) return;

  const lineColor = color ?? getVar('--chart-line-primary', '#1564C0');
  const trendColor = getVar('--text-tertiary', '#7A8594');
  const axisColor = getVar('--text-secondary', '#4A5562');
  const gridColor = getVar('--chart-grid', '#E4E7EB');
  const fractDigs = fractionDigits ?? 1;

  const trend = showTrend ? trendLine(xs, values) : null;

  const xMin = xs[0] ?? 0;
  const xMax = xs[xs.length - 1] ?? 0;
  const spanDays = Math.max(1, Math.round((xMax - xMin) / 86400));
  const stepDays = spanDays <= 7 ? 1 : spanDays <= 30 ? 3 : 10;

  const width = area.clientWidth || 600;
  const height = area.clientHeight || 200;

  const series: uPlot.Series[] = [
    {},
    {
      label,
      stroke: lineColor,
      width: 1.75,
      points: { show: true, size: 5, fill: lineColor, stroke: lineColor },
      value: (_u, v) => (v == null ? '—' : `${v.toFixed(fractDigs)} ${unit}`),
    },
  ];
  if (trend) {
    series.push({
      label: 'Тренд',
      stroke: trendColor,
      width: 1,
      dash: [4, 4],
      points: { show: false },
    });
  }

  const opts: uPlot.Options = {
    width,
    height,
    padding: [12, 16, 8, 48],
    legend: { show: false },
    cursor: { show: true, points: { show: true } },
    scales: {
      x: { time: true },
      y: { auto: true },
    },
    axes: [
      {
        stroke: axisColor,
        font: '11px ui-sans-serif, system-ui, sans-serif',
        grid: { stroke: gridColor, width: 1 },
        space: 70,
        splits: () => xs.filter((_, i) => i % stepDays === 0),
        values: (_u, splits) => splits.map((s) => shortDate(s)),
      },
      {
        label: unit,
        labelSize: 12,
        labelFont: '11px ui-sans-serif, system-ui, sans-serif',
        stroke: axisColor,
        font: '11px ui-sans-serif, system-ui, sans-serif',
        grid: { stroke: gridColor, width: 1 },
        size: 48,
      },
    ],
    series,
  };

  const data: uPlot.AlignedData = trend
    ? [xs, values, trend]
    : [xs, values];
  const plot = new uPlotCtor(opts, data, area);

  const tooltip = h('div', { class: 'chart-tooltip' });
  area.appendChild(tooltip);

  const showTooltip = (idx: number) => {
    const v = values[idx];
    const ts = xs[idx];
    if (ts == null || v == null) {
      tooltip.classList.remove('chart-tooltip--visible');
      return;
    }
    tooltip.textContent = `${fullDate(ts)} · ${v.toFixed(fractDigs)} ${unit}`;
    const x = plot.valToPos(ts, 'x');
    const y = plot.valToPos(v, 'y');
    const w = area.clientWidth;
    tooltip.style.left = `${Math.max(8, Math.min(w - 160, x - 70))}px`;
    tooltip.style.top = `${Math.max(0, y - 40)}px`;
    tooltip.classList.add('chart-tooltip--visible');
  };

  const onMove = () => {
    const idx = plot.cursor.idx;
    if (idx == null) {
      tooltip.classList.remove('chart-tooltip--visible');
      return;
    }
    showTooltip(idx);
  };
  const onLeave = () => tooltip.classList.remove('chart-tooltip--visible');
  plot.over.addEventListener('mousemove', onMove);
  plot.over.addEventListener('mouseleave', onLeave);

  const resize = () => plot.setSize({ width: area.clientWidth, height: area.clientHeight });
  const ro = new ResizeObserver(resize);
  ro.observe(area);
  registerCleanup(() => {
    ro.disconnect();
    plot.over.removeEventListener('mousemove', onMove);
    plot.over.removeEventListener('mouseleave', onLeave);
    plot.destroy();
  });
}
